import { operators as o, common as c } from '@pendo/aggregations';
import { applyCustomSegment } from './common.util';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

type OnboardingTimeToCompleteAggregationParams = {
    appId: c.AppIdType;
    targetType: c.TargetType;
    targetId: c.TrackTypeId;
    firstComparisonRange?: string;
    secondComparisonRange?: string;
    thirdComparisonRange?: string;
    expectedDaysToOnboard: number;
    usesAccounts?: boolean;
    segmentId?: string;
};

export default {
    name: 'OnboardingTimeToCompleteAggregation',
    productArea: PRODUCT_AREAS.IMPACT_BRIEF,
    subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
    responseFormat: 'row',
    build: ({
        appId,
        targetType,
        targetId,
        firstComparisonRange,
        secondComparisonRange,
        thirdComparisonRange,
        expectedDaysToOnboard,
        usesAccounts,
        segmentId,
    }: OnboardingTimeToCompleteAggregationParams) => {
        if (!appId) {
            throw new Error('appId is required for OnboardingTimeToCompleteAggregation');
        }
        if (!firstComparisonRange || !secondComparisonRange) {
            throw new Error('comparisonRange is required for OnboardingTimeToCompleteAggregation');
        }
        const metadataAppPath = `_${appId.toString().replace('-', '_')}`;

        return o.pipeline(
            o.spawn(
                o.pipeline(
                    usesAccounts
                        ? buildAccountBasePipeline(
                            {
                                appId,
                                targetType,
                                targetId,
                                firstComparisonRange,
                                secondComparisonRange,
                                expectedDaysToOnboard,
                                segmentId,
                            },
                            metadataAppPath,
                            'pastMedianTimeToComplete',
                        )
                        : buildVisitorBasePipeline(
                            {
                                appId,
                                targetType,
                                targetId,
                                firstComparisonRange,
                                secondComparisonRange,
                                expectedDaysToOnboard,
                                segmentId,
                            },
                            metadataAppPath,
                            'pastMedianTimeToComplete',
                        ),
                ),
                o.pipeline(
                    usesAccounts
                        ? buildAccountBasePipeline(
                            {
                                appId,
                                targetType,
                                targetId,
                                firstComparisonRange: thirdComparisonRange,
                                secondComparisonRange: firstComparisonRange,
                                expectedDaysToOnboard,
                                segmentId,
                            },
                            metadataAppPath,
                            'presentMedianTimeToComplete',
                        )
                        : buildVisitorBasePipeline(
                            {
                                appId,
                                targetType,
                                targetId,
                                firstComparisonRange: thirdComparisonRange,
                                secondComparisonRange: firstComparisonRange,
                                expectedDaysToOnboard,
                                segmentId,
                            },
                            metadataAppPath,
                            'presentMedianTimeToComplete',
                        ),
                ),
            ),
            o.join('id'),
            o.evalExpression({
                timeToCompletePercentageChange:
                    '((presentMedianTimeToComplete - pastMedianTimeToComplete) / pastMedianTimeToComplete) * 100',
            }),
        );
    },
};

function buildVisitorBasePipeline (
    {
        appId,
        targetType,
        targetId,
        firstComparisonRange,
        secondComparisonRange,
        expectedDaysToOnboard,
        segmentId,
    }: OnboardingTimeToCompleteAggregationParams,
    metadataAppPath: string,
    reduceKey: string,
): c.PipelineData[] {
    return [
        o.sources.visitors({ appId }),
        o.identified('visitorId'),
        segmentId && !['everyoneExcludingAnonymous', 'everyone'].includes(segmentId) ? o.segment(segmentId) : null,
        o.bulkExpand('visitor', 'visitor', 'visitorId'),
        o.select({
            visitorId: 'visitorId',
            firstTimeInApp: `if(isNil(metadata.auto${metadataAppPath}.firstidentifiedvisit), metadata.auto${metadataAppPath}.firstvisit, metadata.auto${metadataAppPath}.firstidentifiedvisit)`,
        }),
        o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp <= ${firstComparisonRange}`),
        o.group(['visitorId'], o.groupField('firstTimeInApp', { min: 'firstTimeInApp' })),
        o.merge(
            ['visitorId'],
            o.mappings({
                timeOfEventCompletion: 'timeOfEventCompletion',
            }),
            o.pipeline(
                o.sources.timespan({
                    targetType,
                    targetId,
                }),
                o.identified('visitorId'),
                o.bulkExpand('visitor', 'visitor', 'visitorId'),
                o.select({
                    visitorId: 'visitorId',
                    timeOfEventCompletion: 'firstTime',
                }),
                o.filter(
                    `timeOfEventCompletion >= ${secondComparisonRange} && timeOfEventCompletion <= ${firstComparisonRange}`,
                ),
                o.group(['visitorId'], o.groupField('timeOfEventCompletion', { min: 'timeOfEventCompletion' })),
            ),
        ),
        o.filter('timeOfEventCompletion != null'),
        o.evaluate({
            diffBetweenStartEnd: 'timeOfEventCompletion - firstTimeInApp',
        }),
        ...filterStartEnd(reduceKey, expectedDaysToOnboard, targetId),
    ];
}

function buildAccountBasePipeline (
    {
        appId,
        targetType,
        targetId,
        firstComparisonRange,
        secondComparisonRange,
        expectedDaysToOnboard,
        segmentId,
    }: OnboardingTimeToCompleteAggregationParams,
    metadataAppPath: string,
    reduceKey: string,
): c.PipelineData[] {
    return [
        o.sources.timespan({ targetType, targetId }),
        o.identified('visitorId'),
        applyCustomSegment(
            o.pipeline(
                o.sources.accounts({ appId }),
                o.select({
                    firstTimeInApp: `metadata.auto${metadataAppPath}.firstvisit`,
                    accountId: 'accountId',
                }),
                o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp <= ${firstComparisonRange}`),
            ),
            segmentId,
        ),
        o.group('accountId', o.groupField('firstTime', { min: 'firstTime' })),
        o.bulkExpand('account', 'account', 'accountId'),
        o.filter('firstTime != null'),
        o.evalExpression({
            firstTimeInApp: `account.auto${metadataAppPath}.firstvisit`,
            diffBetweenStartEnd: 'firstTimeInApp - firstTime',
        }),
        ...filterStartEnd(reduceKey, expectedDaysToOnboard, targetId),
    ];
}

function filterStartEnd (reduceKey: string, expectedDaysToOnboard: number, targetId: c.TrackTypeId): c.PipelineData[] {
    return [
        o.filter(`diffBetweenStartEnd > 0 && diffBetweenStartEnd <= ${expectedDaysToOnboard * 24 * 60 * 60 * 1000}`),
        o.reduce({
            [reduceKey]: o.median('diffBetweenStartEnd'),
        }),
        o.evalExpression({
            id: `"onboardingTimeToCompleteAggregation_${targetId}"`,
        }),
    ];
}
